import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../styles/AuthHeader.module.css";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUnreadCount, loadNotifications } from "../store/ducks/notification.duck";
import { getIniciais, getPrimeiroNome } from "../utils/Utils";
import { ArrowForwardIos } from "@material-ui/icons";
import { avatarMenuOptions, headerMenuOptions } from "../pages/home/MenuConfig";
import { useMediaQuery } from "react-responsive";
import { IconButton } from "@material-ui/core";
import AvatarMenuItem from "./AvatarMenuItem";
import useUnit from "../utils/useUnit";
import { canAccessAnyArea } from "../utils/PermissionUtils";
import { getSubsidiaryOptionLabel } from "../utils/useSubsidiaryFilter";
import { RiBuilding2Line } from "react-icons/ri";

// Id da opção de menu que exibe o contador de notificações não lidas

const NOTIFICATION_MENU_ID = "PlanosDeAcao";

export default function AuthHeader() {
    const menuAvatarRef = useRef(null);
    const menuHeaderRef = useRef([]);

    const [openAvatarMenu, setOpenAvatarMenu] = useState(false);
    const [openHeaderMenu, setOpenHeaderMenu] = useState(null);
    const [avatarMenuAnimation, setAvatarMenuAnimation] = useState("out");
    const [headerMenuAnimation, setHeaderMenuAnimation] = useState("out");

    const history = useHistory();
    const location = useLocation();

    const isMobile = useMediaQuery({ query: "(max-width: 1024px)" });

    const { user } = useSelector((state) => state.auth);

    const HeaderOptions = useMemo(() => headerMenuOptions.filter((option) => canAccessAnyArea(user, option.areas)), [user]);
    const AvatarOptions = useMemo(() => avatarMenuOptions.filter((option) => canAccessAnyArea(user, option.areas)), [user]);
    const { notifications } = useSelector((state) => state.notification);

    const { units, isParent, selectedUnit, readOnly, loadUnits, selectUnit } = useUnit();

    const dispatch = useDispatch();

    const unreadNotifications = getUnreadCount(notifications);

    const toggleAvatarMenu = (to) => {
        if (to) {
            setAvatarMenuAnimation("in");

            setOpenAvatarMenu(true);
        } else {
            setAvatarMenuAnimation("out");

            const timer = setTimeout(() => {
                setOpenAvatarMenu(false);
            }, 300);

            return () => clearTimeout(timer);
        }
    };

    const toggleOutHeaderMenu = () => {
        setHeaderMenuAnimation("out");

        const timer = setTimeout(() => {
            setOpenHeaderMenu(null);
        }, 300);

        return () => clearTimeout(timer);
    };

    const handleClickOnScreen = (event) => {
        if (menuAvatarRef.current && !menuAvatarRef.current.contains(event.target)) {
            toggleAvatarMenu(false);
        }

        if (menuHeaderRef.current && !menuHeaderRef.current.some((e) => e?.contains(event.target))) {
            toggleOutHeaderMenu();
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOnScreen);

        return () => {
            document.removeEventListener("mousedown", handleClickOnScreen);
        };
    }, []);

    useEffect(() => {
        loadNotifications(dispatch).catch(() => { });
    }, [dispatch]);

    useEffect(() => {
        loadUnits();
    }, [loadUnits]);

    const canSwitchUnit = isParent && units.length > 1;

    const unitOptions = useMemo(() => units.map((unit) => ({ id: unit.id, label: getSubsidiaryOptionLabel(unit), unit })), [units]);

    const activeUnitId = selectedUnit?.id ?? units[0]?.id ?? null;


    return (
        <header className={`${styles.header} ${isMobile ? styles.mobile : ""} containerRow`}>
            <div>
                <img src="/images/logo-dark.png" className={styles.logo} alt="Lumi" onClick={() => history.push("/")} />
            </div>

            {!isMobile ? (
                <div className={styles.headerMenuContainer}>
                    {HeaderOptions.map((menuItem, index) => {
                        const hasSubMenu = !menuItem.path && menuItem.subMenu;
                        const isActive = location.pathname.includes(menuItem.path) || (hasSubMenu ? menuItem.subMenu.some((subMenu) => location.pathname.includes(subMenu.path)) : false);

                        return (
                            <>
                                <div
                                    ref={(el) => (menuHeaderRef.current[index] = el)}
                                    key={`menu${menuItem.id}`}
                                    className={`d-flex align-items-center gap-8 ${styles.headerMenuOption} ${isActive ? styles.active : ""}`}
                                    onClick={() => {
                                        if (menuItem.path) {
                                            history.push(menuItem.path);
                                        }

                                        if (hasSubMenu) {
                                            if ((openHeaderMenu || openHeaderMenu?.index === index)) {
                                                toggleOutHeaderMenu();
                                            } else {
                                                setHeaderMenuAnimation("in");
                                                setOpenHeaderMenu({ menuItem, index });
                                            }
                                        }
                                    }}
                                >
                                    {isActive ? (menuItem.activeIcon ?? menuItem.icon) : menuItem.icon}
                                    <span className="tt-14 text-nowrap">{menuItem.label}</span>

                                    {menuItem.id === NOTIFICATION_MENU_ID && unreadNotifications > 0 ? <span className={styles.menuBadge}>{unreadNotifications}</span> : null}

                                    {hasSubMenu ? <ArrowForwardIos style={{ color: "#FFF", rotate: openHeaderMenu ? "270deg" : "90deg", fontSize: 13, fontWeight: 400 }} /> : null}
                                </div>

                                {hasSubMenu ? (
                                    <div
                                        key={`submenu${menuItem.id}`}
                                        className={`${styles.subMenuHeaderContainer} ${headerMenuAnimation === "out" ? styles.fadeOut : styles.fadeIn}`}
                                        style={{
                                            display: openHeaderMenu?.index === index ? "flex" : "none",
                                            top: menuHeaderRef.current[index]?.getBoundingClientRect().bottom,
                                            left: menuHeaderRef.current[index]?.getBoundingClientRect().left,
                                            cursor: "pointer",
                                        }}
                                    >
                                        {openHeaderMenu?.menuItem.subMenu.map((subMenu, index) => {
                                            const isActiveSubMenu = location.pathname.includes(subMenu.path);

                                            return (
                                                <div
                                                    key={`submenu-item${index}`}
                                                    className={isActiveSubMenu ? styles.activeSubMenu : ""}
                                                    onClick={() => {
                                                        if (subMenu.path) {
                                                            history.push(subMenu.path);
                                                            toggleOutHeaderMenu();
                                                        }
                                                    }}
                                                >
                                                    <span className="tt-14 semibold color-white">{subMenu.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </>
                        );
                    })}
                </div>
            ) : null}

            <div className={styles.avatarMenuContainer} ref={menuAvatarRef}>
                {readOnly && !isMobile ? (
                    <div className={styles.unitChip} title={getSubsidiaryOptionLabel(selectedUnit)}>
                        <RiBuilding2Line size={16} color="#0E398D" />
                        <span className="tt-14 medium color-gray-09 mb-0">{getSubsidiaryOptionLabel(selectedUnit)}</span>
                    </div>
                ) : null}

                {isMobile ? (
                    <IconButton onClick={() => toggleAvatarMenu(!openAvatarMenu)}>
                        <i className="fas fa-bars" style={{ fontSize: 20 }} />
                    </IconButton>
                ) : (
                    <div onClick={() => toggleAvatarMenu(!openAvatarMenu)} className="d-flex align-items-center gap-8" style={{ cursor: "pointer" }}>
                        <p className="tt-14 color-gray-09 m-0" style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={user?.company?.ctm_name ?? user?.ctm_name}>
                            {user?.company?.ctm_name ?? user?.ctm_name}
                        </p>

                        <div className={styles.avatar} style={user?.logo ? { backgroundImage: `url(${user?.logo?.url})`, backgroundSize: "cover", backgroundPosition: "center", cursor: "pointer" } : null}>
                            {!user?.logo && <p>{getIniciais(user?.ctm_name)}</p>}
                        </div>

                        <ArrowForwardIos style={{ color: "#1C274C", rotate: "90deg", fontSize: 13, fontWeight: 400 }} />
                    </div>
                )}

                <div className={`${styles.menuAvatar} ${avatarMenuAnimation === "out" ? styles.fadeOut : styles.fadeIn} ${isMobile ? styles.mobile : ""} ${unitOptions.length ? styles.withUnits : ""}`} style={{ display: openAvatarMenu ? "flex" : "none" }}>

                    {unitOptions.length ? (
                        <div className={styles.unitMenuSection}>
                            <span className={`tt-14 medium color-gray-05 ${styles.unitMenuTitle}`}>Unidade</span>

                            {unitOptions.map((option) => (
                                <AvatarMenuItem
                                    showIcon
                                    key={`avatar-menu-unit-${option.id}`}
                                    isMobile={isMobile}
                                    isActive={option.id === activeUnitId}
                                    menuItem={{ icon: <RiBuilding2Line size={18} />, label: option.label }}
                                    onClick={() => {
                                        setOpenAvatarMenu(false);

                                        if (canSwitchUnit) {
                                            selectUnit(option.unit);
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    ) : null}

                    {isMobile
                        ? HeaderOptions.map((menuItem, index) => {
                            if (menuItem.subMenu && menuItem.subMenu.length) {
                                return menuItem.subMenu.map((subMenuItem, i) => {
                                    const isActive = location.pathname.includes(subMenuItem.path);
                                    return (
                                        <AvatarMenuItem
                                            showIcon
                                            isMobile={isMobile}
                                            isActive={isActive}
                                            key={`avatar-menu-item-${index}-${i}`}
                                            menuItem={subMenuItem}
                                            onClick={() => {
                                                setOpenAvatarMenu(false);
                                                history.push(subMenuItem.path);
                                            }}
                                        />
                                    );
                                });
                            } else {
                                const isActive = location.pathname.includes(menuItem.path);
                                return (
                                    <AvatarMenuItem
                                        showIcon
                                        isMobile={isMobile}
                                        isActive={isActive}
                                        key={`avatar-menu-item-${index}`}
                                        menuItem={menuItem}
                                        badge={menuItem.id === NOTIFICATION_MENU_ID ? unreadNotifications : 0}
                                        onClick={() => {
                                            setOpenAvatarMenu(false);
                                            history.push(menuItem.path);
                                        }}
                                    />
                                );
                            }
                        })
                        : null}

                    {AvatarOptions.map((menuItem, index) => {
                        const isActive = location.pathname.includes(menuItem.path);
                        return (
                            <AvatarMenuItem
                                showIcon
                                key={`avatar-menu-itemt-${index}`}
                                isMobile={isMobile}
                                isActive={isActive}
                                menuItem={menuItem}
                                onClick={() => {
                                    setOpenAvatarMenu(false);
                                    history.push(menuItem.path);
                                }}
                            />
                        );
                    })}
                </div>
            </div>
        </header>
    );
}
